import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import TripCard from "@/components/TripCard/TripCard";
import BookingResponse from "@/model/BookingResponse";
import isoDate from "@/utils/isoDate";
import { formatMoney } from "@/utils/pricing";
import "@/pages/MyTripsPage/MyTripsPage.scss";

function MyTripsPage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const location = useLocation();
  const bookedId = (location.state as { bookedId?: string } | null)?.bookedId;

  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(false);

    bookingsApi
      .listMyBookings(session.accessToken)
      .then((result) => {
        if (!cancelled) {
          setBookings(result.content ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  if (!session) {
    return null;
  }

  const today = isoDate.toIsoDate(new Date());
  const upcoming = bookings
    .filter((booking) => (booking.checkOut ?? "") > today)
    .sort((a, b) => (a.checkIn ?? "").localeCompare(b.checkIn ?? ""));
  const past = bookings.filter((booking) => (booking.checkOut ?? "") <= today);

  /** Asks for confirmation, telling the guest exactly how much the cancellation policy refunds. */
  const confirmCancellation = async (
    booking: BookingResponse
  ): Promise<boolean> => {
    if (booking.status === "AWAITING_HOST") {
      return window.confirm(t("trips.cancelRequestConfirm"));
    }
    if (booking.status !== "CONFIRMED") {
      return window.confirm(t("trips.cancelUnpaidConfirm"));
    }
    const quote = await bookingsApi.getCancellationQuote(
      session.accessToken,
      booking.id as string
    );
    const refund = formatMoney(
      quote.refundAmount,
      quote.currency,
      i18n.language
    );
    return window.confirm(
      (quote.refundAmount ?? 0) > 0
        ? t("trips.cancelConfirmRefund", {
            refund,
            percent: quote.refundPercent,
          })
        : t("trips.cancelConfirmNoRefund")
    );
  };

  const handleCancel = async (booking: BookingResponse) => {
    if (!booking.id) {
      return;
    }
    setCancellingId(booking.id);
    setCancelError(false);
    try {
      if (!(await confirmCancellation(booking))) {
        return;
      }
      const updated = await bookingsApi.cancelBooking(
        session.accessToken,
        booking.id
      );
      setBookings((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch {
      setCancelError(true);
    } finally {
      setCancellingId(null);
    }
  };

  const renderTrip = (booking: BookingResponse) => {
    const cancellable =
      (booking.status === "CONFIRMED" ||
        booking.status === "PENDING_PAYMENT" ||
        booking.status === "AWAITING_HOST") &&
      (booking.checkIn ?? "") > today;
    return (
      <TripCard
        key={booking.id}
        booking={booking}
        highlighted={booking.id === bookedId}
        onCancel={cancellable ? () => handleCancel(booking) : undefined}
        isCancelling={cancellingId === booking.id}
      />
    );
  };

  return (
    <div className="my-trips-page">
      <h1>{t("trips.title")}</h1>

      {bookedId && (
        <p className="my-trips-page__success" role="status">
          {t("trips.bookedNotice")}
        </p>
      )}
      {cancelError && (
        <p className="my-trips-page__error" role="alert">
          {t("trips.cancelError")}
        </p>
      )}

      {isLoading && <p>{t("trips.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("trips.error")}</p>}

      {!isLoading && !error && bookings.length === 0 && (
        <div className="my-trips-page__empty">
          <p>{t("trips.empty")}</p>
          <Link to="/">{t("trips.startSearching")}</Link>
        </div>
      )}

      {!isLoading && !error && upcoming.length > 0 && (
        <section className="my-trips-page__section">
          <h2>{t("trips.upcoming")}</h2>
          {upcoming.map(renderTrip)}
        </section>
      )}

      {!isLoading && !error && past.length > 0 && (
        <section className="my-trips-page__section">
          <h2>{t("trips.past")}</h2>
          {past.map(renderTrip)}
        </section>
      )}
    </div>
  );
}

export default MyTripsPage;
